/**
 * The exact `ISODate` time scalar for the S0 keepsake protocol
 * (PC-B rev 9, section 10 preamble).
 *
 * `ISODate` is the UTC fixed-millisecond form `YYYY-MM-DDTHH:mm:ss.sssZ`.
 * At every ingress boundary this parser accepts a valid RFC 3339 instant
 * with an EXPLICIT offset, converts it to UTC, truncates fractional
 * precision after milliseconds toward the earlier instant, and emits only
 * that form. Missing offsets, leap seconds (second 60), invalid calendar
 * values, and instants before 0001-01-01T00:00:00.000Z or after
 * 9999-12-31T23:59:59.999Z REJECT (return null).
 *
 * Implemented without the Date constructor: JS Date misinterprets years
 * below 100 (Date.UTC maps 0-99 to 1900+) and the proleptic-Gregorian
 * epoch-day math below is exactly mirrorable client-side.
 *
 * CROSS-BOUNDARY MIRROR (rule #4): `api/_lib/keepsake/isodate.ts` is the
 * byte-identical server copy. scripts/probe-keepsake-parity.mjs asserts
 * identical output on shared fixtures. Change both in lockstep.
 */

// RFC 3339 date-time with REQUIRED explicit offset ('Z'/'z' or +-HH:MM).
// Lowercase 't'/'z' are valid RFC 3339; the emitted form is always
// uppercase. Fraction has at least one digit when present.
const RFC3339_RE =
  /^(\d{4})-(\d{2})-(\d{2})[Tt](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?([Zz]|[+-]\d{2}:\d{2})$/;

const MS_PER_DAY = 86_400_000;

function isLeapYear(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function daysInMonth(y: number, m: number): number {
  if (m === 2 && isLeapYear(y)) return 29;
  return DAYS_IN_MONTH[m - 1];
}

/**
 * Proleptic-Gregorian civil date -> days since 1970-01-01 (Howard
 * Hinnant's days_from_civil). Exact for the whole 0001..9999 range.
 */
function daysFromCivil(y: number, m: number, d: number): number {
  const yy = m <= 2 ? y - 1 : y;
  const era = Math.floor((yy >= 0 ? yy : yy - 399) / 400);
  const yoe = yy - era * 400;
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

/** Inverse of daysFromCivil (civil_from_days). */
function civilFromDays(z: number): { y: number; m: number; d: number } {
  z += 719468;
  const era = Math.floor((z >= 0 ? z : z - 146096) / 146097);
  const doe = z - era * 146097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const y = yoe + era * 400;
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp + (mp < 10 ? 3 : -9);
  return { y: y + (m <= 2 ? 1 : 0), m, d };
}

// Epoch-ms bounds of the accepted UTC range.
const MIN_EPOCH_MS = daysFromCivil(1, 1, 1) * MS_PER_DAY; // 0001-01-01T00:00:00.000Z
const MAX_EPOCH_MS =
  daysFromCivil(9999, 12, 31) * MS_PER_DAY + (23 * 3600 + 59 * 60 + 59) * 1000 + 999;

function pad(n: number, width: number): string {
  return String(n).padStart(width, '0');
}

/** Epoch milliseconds -> canonical ISODate string. Throws out of range. */
export function isoDateFromEpochMs(epochMs: number): string {
  if (!Number.isSafeInteger(epochMs) || epochMs < MIN_EPOCH_MS || epochMs > MAX_EPOCH_MS) {
    throw new Error('isodate: epoch out of range');
  }
  const days = Math.floor(epochMs / MS_PER_DAY);
  const msOfDay = epochMs - days * MS_PER_DAY;
  const { y, m, d } = civilFromDays(days);
  const hh = Math.floor(msOfDay / 3_600_000);
  const mm = Math.floor((msOfDay % 3_600_000) / 60_000);
  const ss = Math.floor((msOfDay % 60_000) / 1000);
  const ms = msOfDay % 1000;
  return (
    pad(y, 4) + '-' + pad(m, 2) + '-' + pad(d, 2) +
    'T' + pad(hh, 2) + ':' + pad(mm, 2) + ':' + pad(ss, 2) +
    '.' + pad(ms, 3) + 'Z'
  );
}

/**
 * Parse an RFC 3339 instant with explicit offset into UTC epoch
 * milliseconds, truncating the fraction after milliseconds toward the
 * earlier instant. Returns null on ANY rejection (missing offset, leap
 * second, invalid calendar value, out of range).
 */
export function parseRfc3339ToEpochMs(input: unknown): number | null {
  if (typeof input !== 'string') return null;
  const m = RFC3339_RE.exec(input);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const hour = Number(m[4]);
  const minute = Number(m[5]);
  const second = Number(m[6]);
  const fraction = m[7] ?? '';
  const offset = m[8];

  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  if (hour > 23 || minute > 59) return null;
  // Leap seconds reject.
  if (second > 59) return null;

  // Truncate the fraction to milliseconds toward the earlier instant:
  // take the first three digits, zero-padded on the right.
  const msStr = (fraction + '000').slice(0, 3);
  const millis = Number(msStr);

  let offsetMinutes = 0;
  if (offset !== 'Z' && offset !== 'z') {
    const sign = offset[0] === '-' ? -1 : 1;
    const oh = Number(offset.slice(1, 3));
    const om = Number(offset.slice(4, 6));
    if (oh > 23 || om > 59) return null;
    offsetMinutes = sign * (oh * 60 + om);
  }

  const epochMs =
    daysFromCivil(year, month, day) * MS_PER_DAY +
    (hour * 3600 + minute * 60 + second) * 1000 +
    millis -
    offsetMinutes * 60_000;

  if (epochMs < MIN_EPOCH_MS || epochMs > MAX_EPOCH_MS) return null;
  return epochMs;
}

/**
 * The ingress normalizer: RFC 3339 instant with explicit offset -> the
 * canonical ISODate form, or null on rejection. Only the returned form
 * may enter RFC 8785 bodies.
 */
export function normalizeIsoDate(input: unknown): string | null {
  const epochMs = parseRfc3339ToEpochMs(input);
  if (epochMs === null) return null;
  return isoDateFromEpochMs(epochMs);
}

/**
 * True when `v` is ALREADY in the exact canonical ISODate form (a valid
 * instant whose normalization is byte-identical to itself).
 */
export function isCanonicalIsoDate(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  return normalizeIsoDate(v) === v;
}
